/**
 * TradeForm Component
 *
 * Supports two modes:
 * - Buy/Write: single form combining stock purchase and option sale
 * - Separate Transactions: stock purchase form + option sale form
 *
 * Features:
 * - Client-side validation mirroring server rules (immediate feedback)
 * - Broker commission profile selector with auto-population and override
 * - Implied volatility display when option details are entered
 * - Strike price ITM warning with confirmation dialog
 * - Confirmation panel on successful trade creation
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 2.1, 2.2, 2.3, 2.4, 5.2, 16.3, 16.4, 17.1, 17.2
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BuyWriteInput,
  StockPurchaseInput,
  OptionSaleInput,
  ProtectivePutEntryInput,
  BrokerProfile,
  TradePosition,
  ImpliedVolatility,
  Exchange,
  InstrumentClass,
  validateBuyWriteInput,
  validateStockPurchaseInput,
  validateOptionSaleInput,
  validateProtectivePutEntryInput,
  computeCommission,
  selectSchedule,
  type CommissionResult,
  type ValidationError,
} from '@covered-calls/shared';
import type { MaxPositionsLimit, PlanUsage } from '@covered-calls/shared';
import { apiClient } from '../services/api-client';
import type { ApiClientError } from '../services/api-client';
import { getImpliedVolatility } from '../services/market-data-service';
import { createProtectivePut } from '../services/protective-put-service';
import { TrialLimitReachedMessage } from './TrialLimitReachedMessage';
import { UsageIndicator } from './UsageIndicator';

// ─── Types ──────────────────────────────────────────────────────────────────

type TradeMode = 'buy-write' | 'separate' | 'protective-put';
type SeparateStep = 'stock-purchase' | 'option-sale';

interface TradeFormProps {
  /** Optional: existing position ID when selling an option against an existing holding */
  positionId?: string;
  /** Callback fired after successful trade creation */
  onSuccess?: (position: TradePosition) => void;
  /**
   * Optional plan-usage block (from the profile GET `plan` block, Decision 8).
   * When provided, the Usage_Indicator renders proactively near the create
   * action. Supplied by the parent so TradeForm does not issue its own profile
   * fetch. _Requirements: 10.1, 10.2, 10.5_
   */
  planUsage?: PlanUsage;
}

interface ConfirmationData {
  position: TradePosition;
  mode: TradeMode;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  container: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,
  heading: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '16px',
  } as React.CSSProperties,
  tabContainer: {
    display: 'flex',
    gap: '0',
    marginBottom: '24px',
    borderBottom: '2px solid var(--color-border)',
  } as React.CSSProperties,
  tab: (isActive: boolean) => ({
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'var(--color-brand)' : 'var(--color-muted)',
    borderBottom: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.15s ease',
  } as React.CSSProperties),
  fieldGroup: {
    marginBottom: '16px',
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-muted)',
    marginBottom: '4px',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    outline: 'none',
    transition: 'border-color 0.15s ease',
  } as React.CSSProperties,
  inputError: {
    borderColor: 'var(--color-danger)',
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'var(--color-surface)',
  } as React.CSSProperties,
  errorText: {
    color: 'var(--color-danger)',
    fontSize: '0.8rem',
    marginTop: '4px',
  } as React.CSSProperties,
  formError: {
    background: 'var(--color-chip-danger-bg)',
    border: '1px solid var(--color-chip-danger-text)',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '16px',
    color: 'var(--color-chip-danger-text)',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  itmWarning: {
    background: 'var(--color-chip-warning-bg)',
    border: '1px solid var(--color-chip-warning-text)',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '16px',
    color: 'var(--color-chip-warning-text)',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  itmWarningButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  } as React.CSSProperties,
  ivDisplay: {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '16px',
    color: 'var(--color-brand)',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  submitBtn: {
    width: '100%',
    padding: '12px',
    background: 'var(--color-brand)',
    color: 'var(--color-on-brand)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  } as React.CSSProperties,
  submitBtnDisabled: {
    background: 'var(--color-brand-disabled)',
    cursor: 'not-allowed',
  } as React.CSSProperties,
  confirmBtn: (variant: 'primary' | 'secondary') => ({
    padding: '6px 14px',
    border: variant === 'primary' ? 'none' : '1px solid var(--color-input-border)',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    background: variant === 'primary' ? 'var(--color-brand)' : 'var(--color-surface)',
    color: variant === 'primary' ? 'var(--color-on-brand)' : 'var(--color-text)',
  } as React.CSSProperties),
  confirmation: {
    background: 'var(--color-chip-success-bg)',
    border: '1px solid var(--color-chip-success-text)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  } as React.CSSProperties,
  confirmationHeading: {
    color: 'var(--color-chip-success-text)',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '12px',
  } as React.CSSProperties,
  confirmationDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    fontSize: '0.85rem',
    color: 'var(--color-text)',
  } as React.CSSProperties,
  sectionHeading: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--color-border)',
  } as React.CSSProperties,
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  } as React.CSSProperties,
  commissionPreview: {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    padding: '10px 12px',
    marginTop: '4px',
    marginBottom: '8px',
    color: 'var(--color-brand)',
    fontSize: '0.8rem',
  } as React.CSSProperties,
  commissionPreviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  } as React.CSSProperties,
  manualEntryNote: {
    background: 'var(--color-chip-warning-bg)',
    border: '1px solid var(--color-chip-warning-text)',
    borderRadius: '6px',
    padding: '10px 12px',
    marginTop: '4px',
    marginBottom: '8px',
    color: 'var(--color-chip-warning-text)',
    fontSize: '0.8rem',
  } as React.CSSProperties,
  usageRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
    fontSize: '0.8rem',
  } as React.CSSProperties,
};

// ─── Commission preview sub-component ─────────────────────────────────────────

interface CommissionPreviewProps {
  label: string;
  preview: CommissionResult | null;
  scheduleMissing: boolean;
  exchange: Exchange;
  instrumentLabel: string;
}

function CommissionPreview({
  label,
  preview,
  scheduleMissing,
  exchange,
  instrumentLabel,
}: CommissionPreviewProps) {
  if (scheduleMissing) {
    return (
      <div style={styles.manualEntryNote} role="note">
        No commission schedule for {exchange} {instrumentLabel} in this profile — enter commission manually.
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div style={styles.commissionPreview} aria-live="polite">
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{label}</div>
      <div style={styles.commissionPreviewRow}>
        <span>Commission:</span>
        <span>${preview.commission.toFixed(2)}</span>
      </div>
      <div style={styles.commissionPreviewRow}>
        <span>GST:</span>
        <span>${preview.gst.toFixed(2)}</span>
      </div>
      <div style={{ ...styles.commissionPreviewRow, fontWeight: 600 }}>
        <span>Total (stored):</span>
        <span>${preview.total.toFixed(2)}</span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TradeForm({ positionId, onSuccess, planUsage: planUsageProp }: TradeFormProps) {
  // Mode & step state
  const [mode, setMode] = useState<TradeMode>('buy-write');
  const [separateStep, setSeparateStep] = useState<SeparateStep>('stock-purchase');

  // Form fields
  const [ticker, setTicker] = useState('');
  const [exchange, setExchange] = useState<Exchange>(Exchange.ASX);
  const [tradeDate, setTradeDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [strikePrice, setStrikePrice] = useState('');
  const [premium, setPremium] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [commission, setCommission] = useState('');
  const [optionCommission, setOptionCommission] = useState('');

  // Protective-put leg fields (long put)
  const [putStrikePrice, setPutStrikePrice] = useState('');
  const [putPremium, setPutPremium] = useState('');
  const [putExpiryDate, setPutExpiryDate] = useState('');
  const [putCommission, setPutCommission] = useState('');

  // Broker profile
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [commissionOverridden, setCommissionOverridden] = useState(false);

  // Per-leg commission previews from the selected broker profile schedule.
  // `null` = a schedule matched but no preview yet; `undefined`/absent handled below.
  const [stockCommissionPreview, setStockCommissionPreview] = useState<CommissionResult | null>(null);
  const [optionCommissionPreview, setOptionCommissionPreview] = useState<CommissionResult | null>(null);
  // Whether a matching schedule exists for each leg (false => show manual-entry note).
  const [stockScheduleMissing, setStockScheduleMissing] = useState(false);
  const [optionScheduleMissing, setOptionScheduleMissing] = useState(false);

  // Protective-put leg (OPTIONS) commission preview.
  const [putCommissionPreview, setPutCommissionPreview] = useState<CommissionResult | null>(null);
  const [putScheduleMissing, setPutScheduleMissing] = useState(false);

  // Validation & state
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationData | null>(null);

  // Plan usage (Decision 8) — the profile GET `plan` block (passed from the
  // parent) feeds the Usage_Indicator proactively; the limit-reached payload
  // (Decision 9) refreshes it and seeds the message on rejection.
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(planUsageProp ?? null);
  const [limitReached, setLimitReached] = useState<{
    effectiveLimit: MaxPositionsLimit;
    positionsCreated: number;
  } | null>(null);

  // Keep local plan usage in sync when the parent supplies/updates the block.
  useEffect(() => {
    if (planUsageProp) {
      setPlanUsage(planUsageProp);
    }
  }, [planUsageProp]);

  // IV & ITM
  const [impliedVolatility, setImpliedVolatility] = useState<ImpliedVolatility | null>(null);
  const [ivLoading, setIvLoading] = useState(false);
  const [ivError, setIvError] = useState('');
  const [currentStockPrice, setCurrentStockPrice] = useState<number | null>(null);
  const [showItmWarning, setShowItmWarning] = useState(false);
  const [itmConfirmed, setItmConfirmed] = useState(false);

  // Separate transactions — created position for linking option sale
  const [createdPositionId, setCreatedPositionId] = useState<string | null>(positionId ?? null);

  const ivFetchRef = useRef<AbortController | null>(null);

  // ─── Load broker profiles on mount ──────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const loadBrokerProfiles = async () => {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        if (!cancelled) {
          setBrokerProfiles(profiles.filter((p) => p.isActive));
        }
      } catch {
        // Non-blocking — profiles just won't be available
      }
    };
    loadBrokerProfiles();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Auto-populate commission from broker profile ─────────────────────

  useEffect(() => {
    if (!selectedProfileId) {
      setStockCommissionPreview(null);
      setOptionCommissionPreview(null);
      setStockScheduleMissing(false);
      setOptionScheduleMissing(false);
      setPutCommissionPreview(null);
      setPutScheduleMissing(false);
      return;
    }

    const profile = brokerProfiles.find((p) => p.profileId === selectedProfileId);
    if (!profile) {
      setStockCommissionPreview(null);
      setOptionCommissionPreview(null);
      setStockScheduleMissing(false);
      setOptionScheduleMissing(false);
      setPutCommissionPreview(null);
      setPutScheduleMissing(false);
      return;
    }

    const qty = parseInt(quantity) || 0;

    // Whether each leg is relevant to the current mode/step.
    const stockLegShown =
      mode === 'buy-write' || mode === 'protective-put' || separateStep === 'stock-purchase';
    const optionLegShown = mode === 'buy-write' || separateStep === 'option-sale';
    const putLegShown = mode === 'protective-put';

    // ─── Stock leg (SHARES): tradeValue = purchasePrice × quantity ─────
    if (stockLegShown) {
      const stockSchedule = selectSchedule(profile, exchange, InstrumentClass.SHARES);
      if (stockSchedule) {
        const stockTradeValue = (parseFloat(purchasePrice) || 0) * qty;
        const result = computeCommission(stockSchedule, {
          tradeValue: stockTradeValue,
          quantity: qty,
        });
        setStockCommissionPreview(result);
        setStockScheduleMissing(false);
        if (!commissionOverridden) {
          setCommission(result.total.toFixed(2));
        }
      } else {
        setStockCommissionPreview(null);
        setStockScheduleMissing(true);
      }
    } else {
      setStockCommissionPreview(null);
      setStockScheduleMissing(false);
    }

    // ─── Option leg (OPTIONS): tradeValue = premium × quantity ─────────
    if (optionLegShown) {
      const optionSchedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (optionSchedule) {
        const optionTradeValue = (parseFloat(premium) || 0) * qty;
        const result = computeCommission(optionSchedule, {
          tradeValue: optionTradeValue,
          quantity: qty,
        });
        setOptionCommissionPreview(result);
        setOptionScheduleMissing(false);
        if (!commissionOverridden) {
          setOptionCommission(result.total.toFixed(2));
        }
      } else {
        setOptionCommissionPreview(null);
        setOptionScheduleMissing(true);
      }
    } else {
      setOptionCommissionPreview(null);
      setOptionScheduleMissing(false);
    }

    // ─── Long-put leg (OPTIONS): tradeValue = putPremium × quantity ────
    if (putLegShown) {
      const putSchedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (putSchedule) {
        const putTradeValue = (parseFloat(putPremium) || 0) * qty;
        const result = computeCommission(putSchedule, {
          tradeValue: putTradeValue,
          quantity: qty,
        });
        setPutCommissionPreview(result);
        setPutScheduleMissing(false);
        if (!commissionOverridden) {
          setPutCommission(result.total.toFixed(2));
        }
      } else {
        setPutCommissionPreview(null);
        setPutScheduleMissing(true);
      }
    } else {
      setPutCommissionPreview(null);
      setPutScheduleMissing(false);
    }
  }, [
    selectedProfileId,
    exchange,
    purchasePrice,
    quantity,
    premium,
    putPremium,
    brokerProfiles,
    commissionOverridden,
    mode,
    separateStep,
  ]);

  // ─── Fetch IV when option details change ──────────────────────────────

  useEffect(() => {
    if (!ticker || !strikePrice || !expiryDate) {
      setImpliedVolatility(null);
      return;
    }

    // Debounce IV fetch
    const timeout = setTimeout(() => {
      fetchImpliedVolatility();
    }, 500);

    return () => clearTimeout(timeout);
  }, [ticker, strikePrice, expiryDate]);

  const fetchImpliedVolatility = async () => {
    if (ivFetchRef.current) {
      ivFetchRef.current.abort();
    }

    const controller = new AbortController();
    ivFetchRef.current = controller;

    setIvLoading(true);
    setIvError('');

    try {
      const iv = await getImpliedVolatility(
        ticker,
        { strike: parseFloat(strikePrice), expiry: expiryDate },
        { signal: controller.signal },
      );
      setImpliedVolatility(iv);

      // Also fetch current stock price for ITM validation
      try {
        const priceData = await apiClient.get<{ price: number }>(
          `/market/price/${encodeURIComponent(ticker)}`,
          { signal: controller.signal }
        );
        setCurrentStockPrice(priceData.price);
      } catch {
        // Non-blocking — ITM validation just won't work
        setCurrentStockPrice(null);
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Request was cancelled.') {
        setIvError('Implied volatility unavailable');
        setImpliedVolatility(null);
      }
    } finally {
      setIvLoading(false);
    }
  };

  // ─── ITM Warning Detection ────────────────────────────────────────────

  useEffect(() => {
    const strike = parseFloat(strikePrice);
    if (!isNaN(strike) && currentStockPrice !== null && strike < currentStockPrice) {
      setShowItmWarning(true);
      setItmConfirmed(false);
    } else {
      setShowItmWarning(false);
      setItmConfirmed(false);
    }
  }, [strikePrice, currentStockPrice]);

  // ─── Validation ───────────────────────────────────────────────────────

  const getFieldError = (field: string): string | undefined => {
    return errors.find((e) => e.field === field)?.message;
  };

  const validateForm = (): ValidationError[] => {
    if (mode === 'protective-put') {
      const qty = parseInt(quantity);
      const input: ProtectivePutEntryInput = {
        ticker: ticker.trim(),
        exchange,
        tradeDate,
        purchasePrice: parseFloat(purchasePrice),
        quantity: qty,
        putStrikePrice: parseFloat(putStrikePrice),
        putPremium: parseFloat(putPremium),
        putExpiryDate,
        putContracts: Math.floor((qty || 0) / 100),
      };
      return validateProtectivePutEntryInput(input);
    }

    if (mode === 'buy-write') {
      const input: BuyWriteInput = {
        ticker: ticker.trim(),
        exchange,
        tradeDate,
        purchasePrice: parseFloat(purchasePrice),
        quantity: parseInt(quantity),
        strikePrice: parseFloat(strikePrice),
        premium: parseFloat(premium),
        expiryDate,
        commission: parseFloat(commission),
      };
      return validateBuyWriteInput(input);
    }

    if (separateStep === 'stock-purchase') {
      const input: StockPurchaseInput = {
        ticker: ticker.trim(),
        exchange,
        purchaseDate: tradeDate,
        purchasePrice: parseFloat(purchasePrice),
        quantity: parseInt(quantity),
        commission: parseFloat(commission),
      };
      return validateStockPurchaseInput(input);
    }

    // option-sale step
    const input: OptionSaleInput = {
      strikePrice: parseFloat(strikePrice),
      premium: parseFloat(premium),
      expiryDate,
      commission: parseFloat(commission),
    };
    return validateOptionSaleInput(input);
  };

  // ─── Form Submission ──────────────────────────────────────────────────

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitError('');
    setLimitReached(null);
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (validationErrors.length > 0) return;

    // ITM check — require confirmation
    if (showItmWarning && !itmConfirmed && (mode === 'buy-write' || separateStep === 'option-sale')) {
      return; // User needs to confirm ITM first
    }

    setIsSubmitting(true);

    try {
      if (mode === 'protective-put') {
        const qty = parseInt(quantity);
        const input: ProtectivePutEntryInput = {
          ticker: ticker.trim().toUpperCase(),
          exchange,
          tradeDate,
          purchasePrice: parseFloat(purchasePrice),
          quantity: qty,
          putStrikePrice: parseFloat(putStrikePrice),
          putPremium: parseFloat(putPremium),
          putExpiryDate,
          putContracts: Math.floor((qty || 0) / 100),
          stockCommission: parseFloat(commission),
          putCommission: parseFloat(putCommission || commission),
          brokerProfileId: selectedProfileId || undefined,
        };

        const position = await createProtectivePut(input);
        setConfirmation({ position, mode: 'protective-put' });
        onSuccess?.(position);
      } else if (mode === 'buy-write') {
        const input: BuyWriteInput = {
          ticker: ticker.trim().toUpperCase(),
          exchange,
          tradeDate,
          purchasePrice: parseFloat(purchasePrice),
          quantity: parseInt(quantity),
          strikePrice: parseFloat(strikePrice),
          premium: parseFloat(premium),
          expiryDate,
          stockCommission: parseFloat(commission),
          optionCommission: parseFloat(optionCommission || commission),
          brokerProfileId: selectedProfileId || undefined,
        };

        const position = await apiClient.post<TradePosition>('/trades/buy-write', input);
        setConfirmation({ position, mode: 'buy-write' });
        onSuccess?.(position);
      } else if (separateStep === 'stock-purchase') {
        const input: StockPurchaseInput = {
          ticker: ticker.trim().toUpperCase(),
          exchange,
          purchaseDate: tradeDate,
          purchasePrice: parseFloat(purchasePrice),
          quantity: parseInt(quantity),
          commission: parseFloat(commission),
          brokerProfileId: selectedProfileId || undefined,
        };

        const position = await apiClient.post<TradePosition>('/trades/stock-purchase', input);
        setCreatedPositionId(position.positionId);
        // Move to option sale step
        setSeparateStep('option-sale');
        resetOptionFields();
        setConfirmation({ position, mode: 'separate' });
        onSuccess?.(position);
      } else {
        // option-sale step
        const targetId = createdPositionId ?? positionId;
        if (!targetId) {
          setSubmitError('No position selected for option sale.');
          setIsSubmitting(false);
          return;
        }

        const input: OptionSaleInput = {
          strikePrice: parseFloat(strikePrice),
          premium: parseFloat(premium),
          expiryDate,
          commission: parseFloat(commission),
          brokerProfileId: selectedProfileId || undefined,
        };

        const position = await apiClient.post<TradePosition>(
          `/trades/${targetId}/sell-option`,
          input
        );
        setConfirmation({ position, mode: 'separate' });
        onSuccess?.(position);
      }
    } catch (err: unknown) {
      // Trial position limit reached (Decision 9): branch on the stable code,
      // not message text. Seed the limit-reached message and Usage_Indicator
      // from the numeric payload carried on the typed error (Req 9.1, 10.5).
      const limitErr =
        err && typeof err === 'object'
          ? (err as Partial<ApiClientError>)
          : null;
      if (
        limitErr &&
        limitErr.code === 'TRIAL_LIMIT_REACHED' &&
        typeof limitErr.effectiveLimit === 'number' &&
        typeof limitErr.positionsCreated === 'number'
      ) {
        const { effectiveLimit, positionsCreated } = limitErr;
        setLimitReached({ effectiveLimit, positionsCreated });
        setPlanUsage((prev) =>
          prev
            ? { ...prev, effectiveLimit, positionsCreated }
            : { planId: 'trial', effectiveLimit, positionsCreated }
        );
      } else if (err && typeof err === 'object' && 'status' in err) {
        const apiErr = err as { status: number; message: string; errors?: ValidationError[] };
        if (apiErr.errors && apiErr.errors.length > 0) {
          setErrors(apiErr.errors);
        } else {
          setSubmitError(apiErr.message || 'Failed to create trade. Please try again.');
        }
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    mode, separateStep, ticker, exchange, tradeDate, purchasePrice, quantity,
    strikePrice, premium, expiryDate, commission, optionCommission, selectedProfileId,
    putStrikePrice, putPremium, putExpiryDate, putCommission,
    showItmWarning, itmConfirmed, createdPositionId, positionId, onSuccess,
  ]);

  const resetOptionFields = () => {
    setStrikePrice('');
    setPremium('');
    setExpiryDate('');
    setCommission('');
    setOptionCommission('');
    setPutStrikePrice('');
    setPutPremium('');
    setPutExpiryDate('');
    setPutCommission('');
    setSelectedProfileId('');
    setCommissionOverridden(false);
    setImpliedVolatility(null);
    setShowItmWarning(false);
    setItmConfirmed(false);
    setErrors([]);
  };

  const resetForm = () => {
    setTicker('');
    setExchange(Exchange.ASX);
    setTradeDate('');
    setPurchasePrice('');
    setQuantity('');
    resetOptionFields();
    setConfirmation(null);
    setSubmitError('');
    setCreatedPositionId(positionId ?? null);
    setSeparateStep('stock-purchase');
  };

  // ─── Commission override handling ─────────────────────────────────────

  const handleCommissionChange = (value: string) => {
    setCommission(value);
    if (selectedProfileId) {
      setCommissionOverridden(true);
    }
  };

  const handleProfileSelect = (profileId: string) => {
    setSelectedProfileId(profileId);
    setCommissionOverridden(false);
  };

  // ─── Render ───────────────────────────────────────────────────────────

  // Show confirmation panel
  if (confirmation) {
    return (
      <div style={styles.container}>
        <div style={styles.confirmation}>
          <h3 style={styles.confirmationHeading}>Trade Created Successfully</h3>
          <div style={styles.confirmationDetail}>
            <span>Ticker:</span>
            <span><strong>{confirmation.position.ticker}</strong></span>
          </div>
          <div style={styles.confirmationDetail}>
            <span>Quantity:</span>
            <span>{confirmation.position.quantity} shares</span>
          </div>
          <div style={styles.confirmationDetail}>
            <span>Purchase Price:</span>
            <span>${confirmation.position.purchasePrice.toFixed(2)}</span>
          </div>
          {confirmation.position.currentOption && (
            <>
              <div style={styles.confirmationDetail}>
                <span>Strike Price:</span>
                <span>${confirmation.position.currentOption.strikePrice.toFixed(2)}</span>
              </div>
              <div style={styles.confirmationDetail}>
                <span>Premium:</span>
                <span>${confirmation.position.currentOption.premium.toFixed(2)}</span>
              </div>
              <div style={styles.confirmationDetail}>
                <span>Expiry Date:</span>
                <span>{confirmation.position.currentOption.expiryDate}</span>
              </div>
            </>
          )}
          <div style={styles.confirmationDetail}>
            <span>Total Commission:</span>
            <span>${confirmation.position.totalCommissions.toFixed(2)}</span>
          </div>
          <div style={styles.confirmationDetail}>
            <span>Trade Type:</span>
            <span>
              {confirmation.mode === 'buy-write'
                ? 'Buy/Write'
                : confirmation.mode === 'protective-put'
                  ? 'Protective Put'
                  : 'Separate'}
            </span>
          </div>
        </div>
        <button
          style={styles.submitBtn}
          onClick={resetForm}
          type="button"
        >
          Create Another Trade
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>New Trade</h2>

      {/* Mode toggle */}
      {!positionId && (
        <div style={styles.tabContainer} role="tablist" aria-label="Trade mode">
          <button
            role="tab"
            aria-selected={mode === 'buy-write'}
            style={styles.tab(mode === 'buy-write')}
            onClick={() => { setMode('buy-write'); resetForm(); }}
            type="button"
          >
            Buy/Write
          </button>
          <button
            role="tab"
            aria-selected={mode === 'protective-put'}
            style={styles.tab(mode === 'protective-put')}
            onClick={() => { setMode('protective-put'); resetForm(); }}
            type="button"
          >
            Protective Put
          </button>
          <button
            role="tab"
            aria-selected={mode === 'separate'}
            style={styles.tab(mode === 'separate')}
            onClick={() => { setMode('separate'); resetForm(); }}
            type="button"
          >
            Buy Stock
          </button>
        </div>
      )}

      {/* Separate transactions step indicator */}
      {mode === 'separate' && !positionId && (
        <p style={{ fontSize: '0.85rem', color: 'var(--color-muted)', marginBottom: '16px' }}>
          {separateStep === 'stock-purchase'
            ? 'Step 1: Stock Purchase'
            : 'Step 2: Option Sale'}
        </p>
      )}

      {/* Submit error */}
      {submitError && (
        <div style={styles.formError} role="alert">
          {submitError}
        </div>
      )}

      {/* ITM Warning Banner */}
      {showItmWarning && !itmConfirmed && (mode === 'buy-write' || separateStep === 'option-sale') && (
        <div style={styles.itmWarning} role="alert" aria-live="polite">
          <strong>In-the-Money Warning:</strong> The strike price (${parseFloat(strikePrice).toFixed(2)}) is below
          the current market price (${currentStockPrice?.toFixed(2)}). This is an ITM option.
          <div style={styles.itmWarningButtons}>
            <button
              style={styles.confirmBtn('primary')}
              onClick={() => setItmConfirmed(true)}
              type="button"
            >
              Confirm — proceed anyway
            </button>
            <button
              style={styles.confirmBtn('secondary')}
              onClick={() => setStrikePrice('')}
              type="button"
            >
              Clear strike price
            </button>
          </div>
        </div>
      )}

      {/* IV Display */}
      {impliedVolatility && (mode === 'buy-write' || separateStep === 'option-sale') && (
        <div style={styles.ivDisplay} aria-live="polite">
          <strong>Implied Volatility:</strong> {impliedVolatility.value?.toFixed(2) ?? "N/A"}%
        </div>
      )}
      {ivLoading && (
        <div style={{ ...styles.ivDisplay, color: 'var(--color-muted)', background: 'var(--color-surface-2)', borderColor: 'var(--color-border)' }}>
          Loading implied volatility...
        </div>
      )}
      {ivError && (
        <div style={{ ...styles.ivDisplay, color: 'var(--color-chip-warning-text)', background: 'var(--color-chip-warning-bg)', borderColor: 'var(--color-chip-warning-text)' }}>
          {ivError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Stock fields (shown in buy/write mode, protective-put mode, or stock-purchase step) */}
        {(mode === 'buy-write' || mode === 'protective-put' || separateStep === 'stock-purchase') && (
          <>
            <h3 style={styles.sectionHeading}>
              {mode === 'buy-write'
                ? 'Stock & Option Details'
                : mode === 'protective-put'
                  ? 'Stock & Protective Put Details'
                  : 'Stock Purchase Details'}
            </h3>

            <div style={styles.fieldGroup}>
              <label htmlFor="ticker" style={styles.label}>Ticker Symbol *</label>
              <input
                id="ticker"
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="e.g. AAPL"
                style={{ ...styles.input, ...(getFieldError('ticker') ? styles.inputError : {}) }}
                aria-invalid={!!getFieldError('ticker')}
                aria-describedby={getFieldError('ticker') ? 'ticker-error' : undefined}
              />
              {getFieldError('ticker') && (
                <p id="ticker-error" style={styles.errorText}>{getFieldError('ticker')}</p>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="exchange" style={styles.label}>Exchange *</label>
              <select
                id="exchange"
                value={exchange}
                onChange={(e) => setExchange(e.target.value as Exchange)}
                style={{ ...styles.select, ...(getFieldError('exchange') ? styles.inputError : {}) }}
                aria-invalid={!!getFieldError('exchange')}
                aria-describedby={getFieldError('exchange') ? 'exchange-error' : undefined}
              >
                <option value={Exchange.ASX}>ASX</option>
                <option value={Exchange.US}>US</option>
              </select>
              {getFieldError('exchange') && (
                <p id="exchange-error" style={styles.errorText}>{getFieldError('exchange')}</p>
              )}
            </div>

            <div style={styles.row}>
              <div style={styles.fieldGroup}>
                <label htmlFor="tradeDate" style={styles.label}>
                  {mode === 'buy-write' || mode === 'protective-put' ? 'Trade Date *' : 'Purchase Date *'}
                </label>
                <input
                  id="tradeDate"
                  type="date"
                  value={tradeDate}
                  onChange={(e) => setTradeDate(e.target.value)}
                  style={{ ...styles.input, ...(getFieldError('tradeDate') || getFieldError('purchaseDate') ? styles.inputError : {}) }}
                  aria-invalid={!!(getFieldError('tradeDate') || getFieldError('purchaseDate'))}
                />
                {(getFieldError('tradeDate') || getFieldError('purchaseDate')) && (
                  <p style={styles.errorText}>{getFieldError('tradeDate') || getFieldError('purchaseDate')}</p>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="quantity" style={styles.label}>Quantity *</label>
                <input
                  id="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="100"
                  min="1"
                  max="100000"
                  step="1"
                  style={{ ...styles.input, ...(getFieldError('quantity') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('quantity')}
                />
                {getFieldError('quantity') && (
                  <p style={styles.errorText}>{getFieldError('quantity')}</p>
                )}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="purchasePrice" style={styles.label}>Purchase Price (per share) *</label>
              <input
                id="purchasePrice"
                type="number"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                placeholder="0.00"
                min="0.01"
                max="999999.99"
                step="0.01"
                style={{ ...styles.input, ...(getFieldError('purchasePrice') ? styles.inputError : {}) }}
                aria-invalid={!!getFieldError('purchasePrice')}
              />
              {getFieldError('purchasePrice') && (
                <p style={styles.errorText}>{getFieldError('purchasePrice')}</p>
              )}
            </div>
          </>
        )}

        {/* Long-put leg fields (protective-put mode only) */}
        {mode === 'protective-put' && (
          <>
            <h3 style={styles.sectionHeading}>Long Put (Protection)</h3>

            <div style={styles.row}>
              <div style={styles.fieldGroup}>
                <label htmlFor="putStrikePrice" style={styles.label}>Put Strike Price *</label>
                <input
                  id="putStrikePrice"
                  type="number"
                  value={putStrikePrice}
                  onChange={(e) => setPutStrikePrice(e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  max="999999.99"
                  step="0.01"
                  style={{ ...styles.input, ...(getFieldError('putStrikePrice') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('putStrikePrice')}
                />
                {getFieldError('putStrikePrice') && (
                  <p style={styles.errorText}>{getFieldError('putStrikePrice')}</p>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="putPremium" style={styles.label}>Premium (paid, per share) *</label>
                <input
                  id="putPremium"
                  type="number"
                  value={putPremium}
                  onChange={(e) => setPutPremium(e.target.value)}
                  placeholder="0.00"
                  min="0.00"
                  max="999999.99"
                  step="0.01"
                  style={{ ...styles.input, ...(getFieldError('putPremium') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('putPremium')}
                />
                {getFieldError('putPremium') && (
                  <p style={styles.errorText}>{getFieldError('putPremium')}</p>
                )}
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.fieldGroup}>
                <label htmlFor="putExpiryDate" style={styles.label}>Put Expiry Date *</label>
                <input
                  id="putExpiryDate"
                  type="date"
                  value={putExpiryDate}
                  onChange={(e) => setPutExpiryDate(e.target.value)}
                  style={{ ...styles.input, ...(getFieldError('putExpiryDate') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('putExpiryDate')}
                />
                {getFieldError('putExpiryDate') && (
                  <p style={styles.errorText}>{getFieldError('putExpiryDate')}</p>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="putContracts" style={styles.label}>Put Contracts</label>
                <input
                  id="putContracts"
                  type="number"
                  value={quantity ? Math.floor((parseInt(quantity) || 0) / 100) : ''}
                  readOnly
                  placeholder="—"
                  style={{ ...styles.input, background: 'var(--color-surface-2)', color: 'var(--color-muted)' }}
                  aria-describedby="putContracts-note"
                />
                <p id="putContracts-note" style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '4px' }}>
                  Auto-derived as quantity ÷ 100.
                </p>
                {getFieldError('putContracts') && (
                  <p style={styles.errorText}>{getFieldError('putContracts')}</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Option fields (shown in buy/write mode or option-sale step) */}
        {(mode === 'buy-write' || separateStep === 'option-sale') && (
          <>
            {mode === 'separate' && (
              <h3 style={styles.sectionHeading}>Option Sale Details</h3>
            )}

            <div style={styles.row}>
              <div style={styles.fieldGroup}>
                <label htmlFor="strikePrice" style={styles.label}>Strike Price *</label>
                <input
                  id="strikePrice"
                  type="number"
                  value={strikePrice}
                  onChange={(e) => setStrikePrice(e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  max="999999.99"
                  step="0.01"
                  style={{ ...styles.input, ...(getFieldError('strikePrice') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('strikePrice')}
                />
                {getFieldError('strikePrice') && (
                  <p style={styles.errorText}>{getFieldError('strikePrice')}</p>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="premium" style={styles.label}>Premium (per share) *</label>
                <input
                  id="premium"
                  type="number"
                  value={premium}
                  onChange={(e) => setPremium(e.target.value)}
                  placeholder="0.00"
                  min="0.00"
                  max="999999.99"
                  step="0.01"
                  style={{ ...styles.input, ...(getFieldError('premium') ? styles.inputError : {}) }}
                  aria-invalid={!!getFieldError('premium')}
                />
                {getFieldError('premium') && (
                  <p style={styles.errorText}>{getFieldError('premium')}</p>
                )}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="expiryDate" style={styles.label}>Expiry Date *</label>
              <input
                id="expiryDate"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                style={{ ...styles.input, ...(getFieldError('expiryDate') ? styles.inputError : {}) }}
                aria-invalid={!!getFieldError('expiryDate')}
              />
              {getFieldError('expiryDate') && (
                <p style={styles.errorText}>{getFieldError('expiryDate')}</p>
              )}
            </div>
          </>
        )}

        {/* Broker profile & commission (shown in all modes) */}
        <h3 style={styles.sectionHeading}>Commission</h3>

        {brokerProfiles.length > 0 && (
          <div style={styles.fieldGroup}>
            <label htmlFor="brokerProfile" style={styles.label}>Broker Profile</label>
            <select
              id="brokerProfile"
              value={selectedProfileId}
              onChange={(e) => handleProfileSelect(e.target.value)}
              style={styles.select}
            >
              <option value="">— Select broker profile —</option>
              {brokerProfiles.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.brokerName} ({profile.schedules.length} schedule{profile.schedules.length === 1 ? '' : 's'})
                </option>
              ))}
            </select>
            {commissionOverridden && selectedProfileId && (
              <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '4px' }}>
                Commission overridden from broker profile value.
              </p>
            )}
          </div>
        )}

        <div style={styles.fieldGroup}>
          <label htmlFor="commission" style={styles.label}>{mode === 'buy-write' ? 'Stock Leg Commission *' : 'Broker Commission *'}</label>
          <input
            id="commission"
            type="number"
            value={commission}
            onChange={(e) => handleCommissionChange(e.target.value)}
            placeholder="0.00"
            min="0.00"
            max="99999.99"
            step="0.01"
            style={{ ...styles.input, ...(getFieldError('commission') ? styles.inputError : {}) }}
            aria-invalid={!!getFieldError('commission')}
          />
          {getFieldError('commission') && (
            <p style={styles.errorText}>{getFieldError('commission')}</p>
          )}
          {selectedProfileId && (mode === 'buy-write' || mode === 'protective-put' || separateStep === 'stock-purchase') && (
            <CommissionPreview
              label={mode === 'buy-write' ? 'Stock leg (shares)' : mode === 'protective-put' ? 'Stock leg (shares)' : 'Stock purchase (shares)'}
              preview={stockCommissionPreview}
              scheduleMissing={stockScheduleMissing}
              exchange={exchange}
              instrumentLabel="SHARES"
            />
          )}
        </div>

        {mode === 'buy-write' && (
          <div style={styles.fieldGroup}>
            <label htmlFor="optionCommission" style={styles.label}>Option Leg Commission *</label>
            <input
              id="optionCommission"
              type="number"
              value={optionCommission}
              onChange={(e) => setOptionCommission(e.target.value)}
              placeholder="0.00"
              min="0.00"
              max="99999.99"
              step="0.01"
              style={styles.input}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '4px' }}>
              Commission for the call option sale leg.
            </p>
          </div>
        )}

        {mode === 'protective-put' && (
          <div style={styles.fieldGroup}>
            <label htmlFor="putCommission" style={styles.label}>Put Leg Commission *</label>
            <input
              id="putCommission"
              type="number"
              value={putCommission}
              onChange={(e) => setPutCommission(e.target.value)}
              placeholder="0.00"
              min="0.00"
              max="99999.99"
              step="0.01"
              style={styles.input}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '4px' }}>
              Commission for the long put purchase leg.
            </p>
            {selectedProfileId && (
              <CommissionPreview
                label="Put leg (options)"
                preview={putCommissionPreview}
                scheduleMissing={putScheduleMissing}
                exchange={exchange}
                instrumentLabel="OPTIONS"
              />
            )}
          </div>
        )}

        {/* Usage indicator near the create action (Req 10.1, 10.2, 10.5) */}
        {planUsage && (
          <div style={styles.usageRow}>
            <UsageIndicator
              planId={planUsage.planId}
              effectiveLimit={planUsage.effectiveLimit}
              positionsCreated={planUsage.positionsCreated}
            />
          </div>
        )}

        {/* Submit */}
        <button
          id="trade-form-submit"
          type="submit"
          disabled={isSubmitting || (showItmWarning && !itmConfirmed && (mode === 'buy-write' || separateStep === 'option-sale'))}
          style={{
            ...styles.submitBtn,
            ...(isSubmitting ? styles.submitBtnDisabled : {}),
          }}
        >
          {isSubmitting
            ? 'Creating...'
            : mode === 'buy-write'
              ? 'Create Buy/Write Trade'
              : mode === 'protective-put'
                ? 'Create Protective Put Trade'
                : separateStep === 'stock-purchase'
                  ? 'Create Stock Purchase'
                  : 'Sell Option'}
        </button>

        {/* Limit-reached message associated with the create action (Req 9.1, 9.3) */}
        {limitReached && (
          <TrialLimitReachedMessage
            effectiveLimit={limitReached.effectiveLimit}
            positionsCreated={limitReached.positionsCreated}
            createActionId="trade-form-submit"
          />
        )}
      </form>
    </div>
  );
}
